import React, { useEffect, useState } from 'react';

import { AuthContext } from './AuthContext';
import { UserProvider } from './UserProvider';
import { decodeJwtUnsafe, validateGovbrAccessToken } from './govbr';
import { useApi } from '../../hooks/useApi';
import { User } from '../../types/User';

const getAccessTokenFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get('access_token') || params.get('token');
};

const stripTokenFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('access_token');
  url.searchParams.delete('token');
  url.searchParams.delete('redirect');
  window.history.replaceState({}, document.title, url.toString());
};

const readAlgFromToken = (token: string): string | null => {
  try {
    const [h] = token.split('.');
    if (!h) return null;
    const paddedH = h.padEnd(h.length + ((4 - (h.length % 4)) % 4), '=');
    const json = atob(paddedH.replace(/-/g, '+').replace(/_/g, '/'));
    const header = JSON.parse(json);
    return header?.alg ?? null;
  } catch {
    return null;
  }
};

const isAsymmetricAlg = (alg?: string | null) =>
  !!alg && (/^(RS|ES|PS)\d{3}$/i.test(alg) || /^EdDSA$/i.test(alg));

const isLikelyGovBrToken = (token: string, payload: any): boolean => {
  const hasGovBrFields =
    payload?.iss?.includes('gov.br') ||
    payload?.iss?.includes('sso.estaleiro.serpro') ||
    payload?.jti?.length > 20 ||
    payload?.amr?.includes('pwd') ||
    payload?.amr?.includes('2fa');

  const alg = readAlgFromToken(token);
  const isAsymmetric = isAsymmetricAlg(alg);
  return isAsymmetric || hasGovBrFields;
};

const validateToken = async (token: string): Promise<any> => {
  const now = Math.floor(Date.now() / 1000);
  const payload: any = decodeJwtUnsafe(token);

  if (!payload || !payload.sub) throw new Error('Token inválido');
  if (payload.exp && payload.exp <= now) throw new Error('Token expirado');

  if (isLikelyGovBrToken(token, payload)) {
    try {
      const { payload: govbrPayload } = await validateGovbrAccessToken(token);
      return {
        ...govbrPayload,
        relacoesInterface:
          govbrPayload.relacoesInterface ||
          govbrPayload.relacoes_interface ||
          [],
        cnpjs: govbrPayload.cnpjs || [],
        unidade: govbrPayload.unidade || 0,
        cnpj: govbrPayload.cnpj,
        statusInterface:
          govbrPayload.statusInterface || govbrPayload.status_interface,
        tpVinculo: govbrPayload.tpVinculo || govbrPayload.tp_vinculo,
      };
    } catch {
      return payload;
    }
  }

  return payload;
};

export const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const [user, setUser] = useState<User | null>(null);
  const [bootValidated, setBootValidated] = useState<boolean | null>(null);
  const api = useApi();

  const setToken = (token: string) => localStorage.setItem('token', token);
  const clearToken = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const buildBasicUserFromPayload = (payload: any): User => {
    const subNumber =
      typeof payload?.sub === 'number'
        ? payload.sub
        : Number(payload?.sub ?? 0);
    return {
      sub: subNumber,
      nome: String(payload?.name ?? payload?.nome ?? ''),
      email: String(payload?.email ?? ''),
      papel: '',
      permissoes: [],
      relacoesInterface: [],
      cnpjs: [],
    };
  };

  const handleSuccessfulAuth = (token: string, payload: any) => {
    setToken(token);
    const userData = buildBasicUserFromPayload(payload);
    setUser(userData);

    stripTokenFromUrl();
    setBootValidated(true);
  };

  const processAuthentication = async (token: string, source: string) => {
    try {
      const validatedPayload = await validateToken(token);

      handleSuccessfulAuth(token, validatedPayload);
      return true;
    } catch (error: any) {
      if (error.message.includes('expirado')) {
        throw new Error('Sessão expirada. Faça login novamente.');
      } else if (error.message.includes('inválido')) {
        throw new Error('Token de autenticação inválido.');
      } else if (error.message.includes('JWKS')) {
        throw new Error('Erro na validação do certificado digital.');
      } else {
        throw new Error(`Falha na autenticação: ${error.message}`);
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const urlToken = getAccessTokenFromUrl();
        const now = Math.floor(Date.now() / 1000);

        if (urlToken) {
          const payload = await validateToken(urlToken);
          setToken(urlToken);
          setUser(buildBasicUserFromPayload(payload));
          stripTokenFromUrl();
          setBootValidated(true);
          return;
        }

        const saved = localStorage.getItem('token');
        if (saved) {
          try {
            const payload: any = decodeJwtUnsafe(saved);
            if (payload?.sub && (!payload.exp || payload.exp > now)) {
              setUser(buildBasicUserFromPayload(payload));
              setBootValidated(true);
              return;
            }
          } catch {}
          clearToken();
        }

        setBootValidated(false);
      } catch {
        clearToken();
        setBootValidated(false);
      }
    })();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const payload: any = decodeJwtUnsafe(token);
        const now = Math.floor(Date.now() / 1000);
        if (payload?.exp && payload.exp <= now) {
          clearToken();
          window.location.href = '/';
        }
      } catch {
        clearToken();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Login padrão com CPF e senha
  const signin = async (cpf: string, senha: string) => {
    try {
      const data = await api.signin(cpf, senha);

      let tokenToUse = null;
      let refreshTokenToUse = null;

      if (data.access_token) {
        tokenToUse = data.access_token;
      } else if (data.access_token) {
        tokenToUse = data.access_token;
      } else if (data.token) {
        tokenToUse = data.token;
      }

      if (data.refresh_token) {
        refreshTokenToUse = data.refresh_token;
      }
      if (tokenToUse) {
        if (refreshTokenToUse) {
          localStorage.setItem('refresh_token', refreshTokenToUse);
        }
        await processAuthentication(tokenToUse, 'CPF/Senha');
        return true;
      } else {
        throw new Error('Resposta da API inválida - nenhum token encontrado');
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        const msg =
          error?.response?.data?.message || 'Usuário ou senha inválidos.xxx';
        const e = new Error(msg);
        (e as any).response = error.response;
        throw e;
      }
      throw error;
    }
  };

  const signout = async () => {
    try {
      await api.logout();
    } catch {
    } finally {
      clearToken();
      window.location.href = '/';
    }
  };

  if (bootValidated === null) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, signin, signout }}>
      <UserProvider>{children}</UserProvider>
    </AuthContext.Provider>
  );
};
